import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Download, RefreshCw, Play, Loader2, CheckCircle2, XCircle, RotateCcw, Square } from "lucide-react";

const fmt = (v) =>
  v ? new Date(v).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "Never";
const num = (v) => (Number(v) || 0).toLocaleString("en-US");

export default function IngramSyncPanel() {
  const [ingram, setIngram] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [history, setHistory] = useState([]);
  const [ingramDraft, setIngramDraft] = useState({});
  const [pricingDraft, setPricingDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [i, p, h] = await Promise.all([
        api.get("/admin/ingram/settings"),
        api.get("/admin/pricing/settings"),
        api.get("/admin/ingram/history", { params: { limit: 30 } }),
      ]);
      setIngram(i.data);
      setPricing(p.data);
      setHistory(h.data || []);
      setIngramDraft({});
      setPricingDraft({});
    } catch {
      toast.error("Could not load Ingram sync settings");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll history every 3s while a run is queued/running so live-progress shows.
  useEffect(() => {
    const running = history.some((r) => r.status === "running" || r.stage === "queued");
    if (!running) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [history, load]);

  const activeRun = history.find((r) => r.status === "running");

  const saveIngram = async (extra = {}) => {
    const patch = { ...ingramDraft, ...extra };
    if (!Object.keys(patch).length) return;
    setSaving(true);
    try {
      const r = await api.put("/admin/ingram/settings", patch);
      setIngram(r.data);
      setIngramDraft({});
      toast.success("Ingram settings saved");
    } catch {
      toast.error("Could not save Ingram settings");
    } finally {
      setSaving(false);
    }
  };

  const savePricing = async () => {
    if (!Object.keys(pricingDraft).length) return;
    setSaving(true);
    try {
      const r = await api.put("/admin/pricing/settings", pricingDraft);
      setPricing(r.data);
      setPricingDraft({});
      toast.success("Pricing formula saved");
    } catch {
      toast.error("Could not save pricing formula");
    } finally {
      setSaving(false);
    }
  };

  const forceSync = async () => {
    if (!window.confirm("Start a new Ingram sync from zero? Any prior checkpoint will be preserved but not resumed.")) return;
    setSyncing(true);
    try {
      await api.post("/admin/ingram/sync", {});
      toast.success("Sync started from zero. History will update in a moment.");
      setTimeout(load, 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start sync");
    } finally {
      setSyncing(false);
    }
  };

  const resumeRun = async (runId, appliedCount) => {
    if (!window.confirm(`Resume this run from checkpoint? It will pick up after row ${(appliedCount || 0).toLocaleString()} instead of restarting from zero.`)) return;
    setSyncing(true);
    try {
      const r = await api.post(`/admin/ingram/resume/${runId}`, {});
      toast.success(r.data?.message || "Resuming from checkpoint.");
      setTimeout(load, 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resume run");
    } finally {
      setSyncing(false);
    }
  };

  const stopSync = async () => {
    if (!window.confirm("Stop the sync that is currently running? The current checkpoint will be preserved so you can resume from where it left off.")) return;
    setSyncing(true);
    try {
      const r = await api.post("/admin/ingram/stop", {});
      toast.success(r.data?.message || "Stop signal sent.");
      setTimeout(load, 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not stop sync");
    } finally {
      setSyncing(false);
    }
  };

  const downloadReport = async (runId) => {
    try {
      const r = await api.get(`/admin/ingram/history/${runId}/report.csv`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ingram-sync-${runId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // responseType is "blob", so an error body arrives as a Blob too and
      // err.response.data.detail is undefined until we read it back as text.
      let msg = "Could not download report";
      try {
        const body = err?.response?.data;
        if (body && typeof body.text === "function") {
          const parsed = JSON.parse(await body.text());
          if (parsed?.detail) msg = parsed.detail;
        } else if (body?.detail) {
          msg = body.detail;
        }
      } catch {
        /* keep the generic message */
      }
      toast.error(msg);
    }
  };

  if (!ingram || !pricing) {
    return <div className="mt-8 font-mono text-xs uppercase text-[#1A1714]/60" data-testid="ingram-loading">Loading…</div>;
  }

  const v = (obj, key, fallback = "") => (key in obj ? obj[key] : ingram[key] ?? fallback);

  const Field = ({ label, testid, children, hint }) => (
    <div className="space-y-1">
      <label className="block font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-[#1A1714]/50">{hint}</p>}
    </div>
  );

  const Number_ = ({ obj, draft, setDraft, k, step = "0.01", suffix }) => (
    <div className="relative">
      <Input
        type="number"
        step={step}
        value={k in draft ? draft[k] : (obj[k] ?? "")}
        onChange={(e) => setDraft({ ...draft, [k]: e.target.value === "" ? "" : Number(e.target.value) })}
        className="rounded-none h-10"
        data-testid={`ingram-${k}`}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#1A1714]/50">{suffix}</span>}
    </div>
  );

  return (
    <div className="mt-8 space-y-8 max-w-4xl" data-testid="ingram-panel">
      <header className="border-b border-[#E5E1D8] pb-4">
        <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">Integrations</p>
        <h2 className="font-serif text-3xl mt-1">Ingram inventory sync</h2>
        <p className="text-sm text-[#1A1714]/70 mt-2 max-w-2xl">
          Daily SFTP sync of the <code className="font-mono text-xs">cat_ing_stock.zip</code> catalog file from Ingram Book Company. Every ISBN is
          matched against your catalog; the rule engine marks each book in-stock or
          out-of-stock, computes purchase price, and (optionally) sets sale price
          using the formula below.
        </p>
      </header>

      {/* Active-sync live progress banner */}
      {activeRun && (
        <div className="border border-blue-300 bg-blue-50 p-4 flex items-start gap-3" data-testid="ingram-active-banner">
          <Loader2 size={18} className="animate-spin text-blue-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-mono text-[10px] uppercase tracking-editorial text-blue-700">
              Sync in progress · stage: {activeRun.stage || "starting"}
            </p>
            <p className="font-serif text-lg mt-0.5 text-blue-900" data-testid="ingram-active-stage-msg">
              {activeRun.stage_message || "Working…"}
            </p>
            {activeRun.records_processed > 0 && (
              <p className="text-xs text-blue-700 mt-1">
                Processed {num(activeRun.records_processed)} rows · {num(activeRun.in_stock)} in stock · {num(activeRun.out_of_stock)} OOS · {num(activeRun.updated_in_db)} DB updates
              </p>
            )}
          </div>
        </div>
      )}

      {/* Last-error banner from the most recent failed run */}
      {(() => {
        const lastFailed = history.find((r) => r.status === "failed");
        if (!lastFailed) return null;
        return (
          <div className="border border-red-300 bg-red-50 p-4" data-testid="ingram-last-error-banner">
            <p className="font-mono text-[10px] uppercase tracking-editorial text-red-700">Last sync failed</p>
            <p className="font-serif text-sm mt-1 text-red-900">{lastFailed.stage_message || lastFailed.error || "Unknown error"}</p>
            {lastFailed.error && lastFailed.error !== lastFailed.stage_message && (
              <p className="text-xs text-red-700 mt-1 font-mono">{lastFailed.error}</p>
            )}
          </div>
        );
      })()}

      {/* Enable + Force sync + credentials */}
      <section className="border border-[#E5E1D8] p-6 space-y-6" data-testid="ingram-config">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">Sync status</p>
            <p className="font-serif text-xl mt-1">{ingram.enabled ? "Enabled" : "Disabled"}</p>
            <p className="text-xs text-[#1A1714]/60 mt-1">
              Runs daily at {String(ingram.schedule_hour).padStart(2, "0")}:{String(ingram.schedule_minute).padStart(2, "0")} {ingram.timezone}.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={ingram.enabled}
              onCheckedChange={(val) => saveIngram({ enabled: val })}
              disabled={saving}
              data-testid="ingram-enable-toggle"
            />
            {activeRun && (
              <Button
                onClick={stopSync}
                disabled={syncing}
                variant="outline"
                className="rounded-none border-red-700 text-red-700 hover:bg-red-50 font-mono text-xs uppercase tracking-editorial"
                data-testid="ingram-stop-sync"
              >
                <Square size={12} className="mr-1" />
                Stop sync
              </Button>
            )}
            <Button
              onClick={forceSync}
              disabled={syncing || !ingram.password_configured || !!activeRun}
              className="rounded-none bg-[#1A1714] hover:bg-[#7A2021] text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial"
              data-testid="ingram-force-sync"
            >
              {syncing ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Play size={12} className="mr-1" />}
              Start from zero
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="SFTP host" testid="host">
            <Input value={v(ingramDraft, "host")} onChange={(e) => setIngramDraft({ ...ingramDraft, host: e.target.value })} className="rounded-none h-10" data-testid="ingram-host" />
          </Field>
          <Field label="Port">
            <Input type="number" value={v(ingramDraft, "port")} onChange={(e) => setIngramDraft({ ...ingramDraft, port: Number(e.target.value) })} className="rounded-none h-10" data-testid="ingram-port" />
          </Field>
          <Field label="Username">
            <Input value={v(ingramDraft, "username")} onChange={(e) => setIngramDraft({ ...ingramDraft, username: e.target.value })} className="rounded-none h-10" data-testid="ingram-username" />
          </Field>
          <Field label="Password" hint={ingram.password_configured ? "Password stored securely — leave blank to keep." : "Password not set — paste your SFTP password."}>
            <Input type="password" placeholder={ingram.password_configured ? "•••••••• (unchanged)" : "SFTP password"} value={ingramDraft.password || ""} onChange={(e) => setIngramDraft({ ...ingramDraft, password: e.target.value })} className="rounded-none h-10" data-testid="ingram-password" />
          </Field>
          <Field label="Remote file path" hint="/Inventory/Catalog_Stock/cat_ing_stock.zip">
            <Input value={v(ingramDraft, "remote_path")} onChange={(e) => setIngramDraft({ ...ingramDraft, remote_path: e.target.value })} className="rounded-none h-10" data-testid="ingram-remote-path" />
          </Field>
          <Field label="Timezone" hint="Any IANA name, e.g. America/Chicago">
            <Input value={v(ingramDraft, "timezone")} onChange={(e) => setIngramDraft({ ...ingramDraft, timezone: e.target.value })} className="rounded-none h-10" data-testid="ingram-timezone" />
          </Field>
          <Field label="Schedule hour (0-23)">
            <Input type="number" min="0" max="23" value={v(ingramDraft, "schedule_hour")} onChange={(e) => setIngramDraft({ ...ingramDraft, schedule_hour: Number(e.target.value) })} className="rounded-none h-10" data-testid="ingram-schedule-hour" />
          </Field>
          <Field label="Schedule minute (0-59)">
            <Input type="number" min="0" max="59" value={v(ingramDraft, "schedule_minute")} onChange={(e) => setIngramDraft({ ...ingramDraft, schedule_minute: Number(e.target.value) })} className="rounded-none h-10" data-testid="ingram-schedule-minute" />
          </Field>
          <Field label="Retry attempts on failure">
            <Input type="number" min="1" max="10" value={v(ingramDraft, "retry_attempts")} onChange={(e) => setIngramDraft({ ...ingramDraft, retry_attempts: Number(e.target.value) })} className="rounded-none h-10" data-testid="ingram-retry-attempts" />
          </Field>
          <Field label="Retry interval (minutes)">
            <Input type="number" min="1" value={v(ingramDraft, "retry_interval_minutes")} onChange={(e) => setIngramDraft({ ...ingramDraft, retry_interval_minutes: Number(e.target.value) })} className="rounded-none h-10" data-testid="ingram-retry-interval" />
          </Field>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={() => saveIngram()}
            disabled={saving || Object.keys(ingramDraft).length === 0}
            className="rounded-none bg-[#1A1714] text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial"
            data-testid="ingram-save-config"
          >
            Save credentials & schedule
          </Button>
        </div>
      </section>

      {/* Pricing formula */}
      <section className="border border-[#E5E1D8] p-6 space-y-4" data-testid="pricing-formula">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">Sale price formula</p>
          <h3 className="font-serif text-2xl mt-1">Purchase price → Sale price</h3>
          <p className="text-xs text-[#1A1714]/60 mt-2 max-w-2xl">
            Applied automatically to every book synced from Ingram. Purchase price is Ingram list × (1 − discount) plus the Ingram per-unit fee.
            Then we add your profit margin, then Stripe fees, and round to 2 decimals.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Stripe %" hint="Applied on marked-up price">
            <Number_ obj={pricing} draft={pricingDraft} setDraft={setPricingDraft} k="stripe_percent" suffix="%" />
          </Field>
          <Field label="Stripe fixed fee" hint="Per transaction (USD)">
            <Number_ obj={pricing} draft={pricingDraft} setDraft={setPricingDraft} k="stripe_fixed" suffix="$" />
          </Field>
          <Field label="Ingram per-unit fee" hint="Per unit charged by Ingram (USD)">
            <Number_ obj={pricing} draft={pricingDraft} setDraft={setPricingDraft} k="ingram_per_unit_fee" suffix="$" />
          </Field>
          <Field label="Profit margin type">
            <div className="flex gap-2">
              {["percent", "fixed"].map((t) => {
                const active = ("profit_margin_type" in pricingDraft ? pricingDraft.profit_margin_type : pricing.profit_margin_type || "percent") === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setPricingDraft({ ...pricingDraft, profit_margin_type: t })}
                    className={"flex-1 h-10 rounded-none border font-mono text-xs uppercase " + (active ? "bg-[#1A1714] text-[#F9F6F0] border-[#1A1714]" : "border-[#E5E1D8] text-[#1A1714]/70 hover:border-[#1A1714]")}
                    data-testid={`ingram-margin-type-${t}`}
                  >
                    {t === "percent" ? "%" : "$ fixed"}
                  </button>
                );
              })}
            </div>
          </Field>
          {(("profit_margin_type" in pricingDraft ? pricingDraft.profit_margin_type : pricing.profit_margin_type) || "percent") === "percent" ? (
            <Field label="Profit margin %" hint="Applied on purchase price">
              <Number_ obj={pricing} draft={pricingDraft} setDraft={setPricingDraft} k="profit_margin_percent" suffix="%" />
            </Field>
          ) : (
            <Field label="Profit margin $" hint="Flat markup per book (USD)">
              <Number_ obj={pricing} draft={pricingDraft} setDraft={setPricingDraft} k="profit_margin_fixed" suffix="$" />
            </Field>
          )}
        </div>

        <div className="mt-4 border-t border-[#E5E1D8]/60 pt-4">
          <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50 mb-2">Formula</p>
          <ol className="text-xs text-[#1A1714]/80 space-y-1 list-decimal ml-5">
            <li>Base purchase price = Ingram list price × (1 − discount)</li>
            <li>Purchase price = Base purchase price + Ingram per-unit fee</li>
            <li>After margin = Purchase price + (margin % of purchase OR fixed $ per book)</li>
            <li>Sale price = After margin × (1 + Stripe %) + Stripe fixed fee</li>
            <li>Rounded to 2 decimal places</li>
          </ol>
        </div>

        <div className="flex items-center justify-between pt-2">
          <label className="flex items-center gap-2 text-sm text-[#1A1714]/80">
            <Switch
              checked={"auto_update_sale_price" in pricingDraft ? pricingDraft.auto_update_sale_price : pricing.auto_update_sale_price}
              onCheckedChange={(val) => setPricingDraft({ ...pricingDraft, auto_update_sale_price: val })}
              data-testid="ingram-auto-update-sale"
            />
            Update sale price during sync
          </label>
          <Button
            onClick={savePricing}
            disabled={saving || Object.keys(pricingDraft).length === 0}
            className="rounded-none bg-[#1A1714] text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial"
            data-testid="ingram-save-pricing"
          >
            Save formula
          </Button>
        </div>
      </section>

      {/* Sync history */}
      <section className="border border-[#E5E1D8] p-6 space-y-4" data-testid="ingram-history">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">Sync history</p>
            <h3 className="font-serif text-2xl mt-1">Last {history.length} runs</h3>
            <p className="text-xs text-[#1A1714]/50 mt-1">Keeping the most recent {ingram.history_retention_runs} runs.</p>
          </div>
          <Button
            onClick={load}
            variant="outline"
            className="rounded-none font-mono text-xs uppercase tracking-editorial border-[#E5E1D8]"
            data-testid="ingram-history-refresh"
          >
            <RefreshCw size={12} className="mr-1" /> Refresh
          </Button>
        </div>

        {history.length === 0 ? (
          <p className="text-sm text-[#1A1714]/60 italic">No syncs yet — press <span className="font-medium">Force sync now</span> to run the first one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1180px] text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50 border-b border-[#E5E1D8]">
                  <th className="py-2 pr-3 whitespace-nowrap">Started</th>
                  <th className="py-2 pr-3 whitespace-nowrap">Trigger</th>
                  <th className="py-2 pr-3 whitespace-nowrap">Status</th>
                  <th className="py-2 pr-3 text-right whitespace-nowrap" title="Books in your catalogue">Catalogue</th>
                  <th className="py-2 pr-3 text-right whitespace-nowrap" title="Feed rows that matched a book in your catalogue">Matched</th>
                  <th className="py-2 pr-3 text-right whitespace-nowrap" title="In your catalogue, absent from the feed, still had stock: set to out of stock">Swept</th>
                  <th className="py-2 pr-3 text-right whitespace-nowrap" title="In your catalogue, absent from the feed, already at zero stock: left untouched">Untouched</th>
                  <th className="py-2 pr-3 text-right whitespace-nowrap" title="Matched + swept. The books this run actually evaluated or confirmed">Evaluated</th>
                  <th className="py-2 pr-3 text-right whitespace-nowrap">In stock</th>
                  <th className="py-2 pr-3 text-right whitespace-nowrap" title="Includes the untouched books, so this agrees with the catalogue total">Final OOS</th>
                  <th className="py-2 pr-3 text-right whitespace-nowrap">DB rows</th>
                  <th className="py-2 whitespace-nowrap">Report</th>
                </tr>
              </thead>
              <tbody>
                {history.map((r) => (
                  <tr key={r.id || r.started_at} className="border-b border-[#E5E1D8]/60" data-testid={`ingram-history-row-${r.id}`}>
                    <td className="py-2 pr-3 whitespace-nowrap">{fmt(r.started_at)}</td>
                    <td className="py-2 pr-3 capitalize">{r.triggered_by}</td>
                    <td className="py-2 pr-3">
                      {r.status === "success" && <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={12} /> Success</span>}
                      {r.status === "failed" && <span className="inline-flex items-center gap-1 text-red-700" title={r.error}><XCircle size={12} /> Failed</span>}
                      {r.status === "running" && <span className="inline-flex items-center gap-1 text-slate-600"><Loader2 size={12} className="animate-spin" /> Running</span>}
                      {r.status === "cancelled" && <span className="inline-flex items-center gap-1 text-amber-700" title={r.error}><Square size={12} /> Cancelled</span>}
                      {r.status === "skipped" && <span className="inline-flex items-center gap-1 text-slate-500">Skipped</span>}
                      {r.resume_of_run_id && (
                        <span className="ml-1 text-[10px] font-mono uppercase text-[#1A1714]/40" title={`Resumed from ${r.resume_of_run_id} at row ${(r.resumed_from_applied_count || 0).toLocaleString()}`}>
                          · resumed
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.catalogue_total == null ? "\u2014" : num(r.catalogue_total)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{num(r.matched_feed_rows != null ? r.matched_feed_rows : r.records_processed)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.swept_to_oos == null ? "\u2014" : num(r.swept_to_oos)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.untouched_already_oos == null ? "\u2014" : num(r.untouched_already_oos)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.total_evaluated_or_confirmed == null ? "\u2014" : num(r.total_evaluated_or_confirmed)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-emerald-700">{num(r.final_in_stock != null ? r.final_in_stock : r.in_stock)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-slate-500">{num(r.final_out_of_stock != null ? r.final_out_of_stock : r.out_of_stock)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{num(r.updated_in_db)}</td>
                    <td className="py-2">
                      <div className="flex items-center gap-3 whitespace-nowrap">
                        {r.status === "success" && (
                          <button
                            onClick={() => downloadReport(r.id)}
                            className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-editorial text-[#7A2021] hover:underline"
                            data-testid={`ingram-download-${r.id}`}
                          >
                            <Download size={11} /> CSV
                          </button>
                        )}
                        {(r.status === "failed" || r.status === "cancelled") && r.checkpoint_last_isbn && (
                          <button
                            onClick={() => resumeRun(r.id, r.checkpoint_applied_count)}
                            disabled={syncing || !!activeRun}
                            className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-editorial text-emerald-800 hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`ingram-resume-${r.id}`}
                            title={`Resume from row ${(r.checkpoint_applied_count || 0).toLocaleString()}`}
                          >
                            <RotateCcw size={11} /> Resume
                          </button>
                        )}
                        {r.status !== "success" && !((r.status === "failed" || r.status === "cancelled") && r.checkpoint_last_isbn) && (
                          <span className="text-xs text-[#1A1714]/40">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {history.some((r) => r.status === "failed") && (
          <p className="text-xs text-red-700 mt-2" data-testid="ingram-last-error">
            Last error:{" "}
            {history.find((r) => r.status === "failed")?.error || "unknown"}
          </p>
        )}
      </section>
    </div>
  );
}
